/**
 * Pure helpers over intakes and templates: lifecycle stages for the explorer,
 * titles and status labels, step counting the way the client experiences it.
 * No React, so the grouping and labels are unit-testable.
 */

import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate,
  OutgoingIntakeStatus,
  OutgoingIntakeTemplate
} from "@/api/types/outgoingIntake";
import { agentLabelAt } from "@/features/agents/types";
import { flattenIntakeStepNodes } from "@/features/foreignNationalOutgoingIntakes/intakeStepFlattening";

import type { RequestArtifact } from "./types";

/** What the Requests IDE calls the agent in its right pane, everywhere. */
export const ASSISTANT_NAME = agentLabelAt("assistant", "client");

export type Stage =
  | "drafts"
  | "with_client"
  | "awaiting_review"
  | "done"
  | "expired";

export const STAGES: readonly {
  id: Stage;
  label: string;
  statuses: OutgoingIntakeStatus[];
}[] = [
  { id: "drafts", label: "Drafts", statuses: ["draft"] },
  {
    id: "with_client",
    label: "With Client",
    statuses: ["sent", "in_progress", "revision_requested"]
  },
  {
    id: "awaiting_review",
    label: "Awaiting Review",
    statuses: ["awaiting_review"]
  },
  { id: "done", label: "Done", statuses: ["accepted"] },
  { id: "expired", label: "Expired", statuses: ["expired"] }
];

export const STATUS_LABEL: Record<OutgoingIntakeStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  in_progress: "In Progress",
  awaiting_review: "Awaiting Review",
  revision_requested: "Changes Requested",
  accepted: "Accepted",
  expired: "Expired"
};

export const stageOf = (status: OutgoingIntakeStatus): Stage =>
  STAGES.find((s) => s.statuses.includes(status))?.id ?? "drafts";

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "object") {
    const v = value as { seconds?: number; toDate?: () => Date };
    if (typeof v.toDate === "function") return v.toDate();
    // Serialized Firestore timestamps carry `_seconds`.
    const fallback = Reflect.get(value, "_seconds") as unknown;
    const seconds =
      v.seconds ?? (typeof fallback === "number" ? fallback : undefined);
    if (typeof seconds === "number") return new Date(seconds * 1000);
  }
  return null;
};

export const asDate = toDate;

const relative = (date: Date, now: Date): string => {
  const ms = date.getTime() - now.getTime();
  const days = Math.round(Math.abs(ms) / 86_400_000);
  const future = ms > 0;
  if (days === 0) return future ? "today" : "today";
  if (days === 1) return future ? "tomorrow" : "yesterday";
  if (days < 30) return future ? `in ${days}d` : `${days}d ago`;
  const months = Math.round(days / 30);
  return future ? `in ${months}mo` : `${months}mo ago`;
};

/** "today 10:12", "yesterday 10:12", "Oct 2 10:12". */
export const clockLabel = (value: unknown, now: Date = new Date()): string => {
  const d = toDate(value);
  if (!d) return "";
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  const day = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return `today ${time}`;
  if (diff === 1) return `yesterday ${time}`;
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {})
  });
  return `${date} ${time}`;
};

/** "just now", "2m ago", "3h ago", then the day-level phrases. */
const ago = (date: Date, now: Date): string => {
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 12 * 60) return `${Math.round(minutes / 60)}h ago`;
  return relative(date, now);
};

/** "10:12" today, otherwise "Oct 2". */
const whenShort = (date: Date, now: Date): string =>
  date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const WITH_CLIENT = new Set<OutgoingIntakeStatus>([
  "sent",
  "in_progress",
  "revision_requested"
]);

/** The status as the attorney reads it, including cancelled and revoked links. */
export const displayStatus = (intake: OutgoingIntake): string => {
  if (intake.cancelled_at) return "Cancelled";
  if (intake.link_revoked_at && WITH_CLIENT.has(intake.status))
    return "Link revoked";
  return STATUS_LABEL[intake.status];
};

export type IntakeStatusLine = {
  text: string;
  tone: "muted" | "warning" | "danger";
};

const answeredOf = (
  intake: OutgoingIntake
): { answered: number; total: number } => {
  const answered = new Set<string>();
  for (const a of intake.progress_answers ?? []) {
    const v = a.value;
    const empty =
      !a.files?.length &&
      (v === null ||
        v === undefined ||
        v === "" ||
        (Array.isArray(v) && !v.length));
    if (!empty)
      answered.add(
        a.repeatable_group_id
          ? String(a.repeatable_group_id)
          : String(a.question_id)
      );
  }
  const steps = flattenIntakeStepNodes(intake.schema ?? []);
  const done = steps.filter((s) => {
    if (s.node_type === "repeatable_group" || s.node_type === "question")
      return answered.has(String(s.node_id));
    return (s.children ?? []).some((c) => answered.has(String(c.node_id)));
  }).length;
  return { answered: done, total: steps.length };
};

/** One phrase about where an intake stands, for the explorer row. */
export const intakeStatusLine = (
  intake: OutgoingIntake,
  now: Date = new Date()
): IntakeStatusLine => {
  const muted = (text: string): IntakeStatusLine => ({ text, tone: "muted" });
  const expires = toDate(intake.expires_at);
  const updated = toDate(intake.updated_at) ?? toDate(intake.created_at);
  const events = intake.events ?? [];
  const lastOf = (kind: string) =>
    toDate([...events].reverse().find((e) => e.kind === kind)?.at);

  if (intake.cancelled_at) return muted("cancelled");
  if (intake.status === "draft")
    return muted(updated ? `edited ${ago(updated, now)}` : "draft");

  if (WITH_CLIENT.has(intake.status)) {
    if (intake.link_revoked_at)
      return { text: "link revoked", tone: "warning" };
    if (intake.email_bounced_at)
      return { text: "email bounced", tone: "danger" };
    const soon = expires && expires.getTime() - now.getTime() < 2 * 86_400_000;
    const tail = soon && expires ? ` · expires ${relative(expires, now)}` : "";
    const tone = soon ? "warning" : "muted";
    if (intake.status === "revision_requested")
      return { text: `changes requested${tail}`, tone };
    if (!intake.opened_at && !intake.progress_answers?.length)
      return { text: `never opened${tail}`, tone };
    const { answered, total } = answeredOf(intake);
    return {
      text: `${
        answered && total ? `${Math.min(answered, total)} of ${total} · ` : ""
      }opened${tail}`,
      tone
    };
  }

  if (intake.status === "awaiting_review") {
    const revision = events.filter((e) => e.kind === "submitted").length;
    const at = lastOf("submitted") ?? toDate(intake.completed_at) ?? updated;
    const when = at ? whenShort(at, now) : "";
    return muted(
      revision > 1
        ? `Submission #${revision}${when ? ` · ${when}` : ""}`
        : `submitted${when ? ` ${when}` : ""}`
    );
  }
  if (intake.status === "accepted") {
    const filed = lastOf("filed");
    if (filed) return muted(`filed ${whenShort(filed, now)}`);
    const at = lastOf("accepted") ?? toDate(intake.completed_at) ?? updated;
    return muted(at ? `accepted ${whenShort(at, now)}` : "accepted");
  }
  if (intake.status === "expired")
    return muted(expires ? `expired ${relative(expires, now)}` : "expired");
  return muted("");
};

/** The status line's text alone. */
export const intakeMeta = (
  intake: OutgoingIntake,
  now: Date = new Date()
): string => intakeStatusLine(intake, now).text;

/** Intakes grouped by stage, newest first within each. */
export const groupByStage = (
  intakes: OutgoingIntake[]
): { stage: (typeof STAGES)[number]; items: OutgoingIntake[] }[] => {
  const sorted = intakes.slice().sort((a, b) => {
    const at = toDate(a.updated_at ?? a.created_at)?.getTime() ?? 0;
    const bt = toDate(b.updated_at ?? b.created_at)?.getTime() ?? 0;
    return bt - at;
  });
  return STAGES.map((stage) => ({
    stage,
    items: sorted.filter((i) => stage.statuses.includes(i.status))
  }));
};

export const countQuestions = (
  nodes: OutgoingIntakeFormNode[]
): { information: number; documents: number } => {
  let information = 0;
  let documents = 0;
  const walk = (list: OutgoingIntakeFormNode[]) => {
    for (const node of list) {
      if (node.node_type === "question") {
        if (node.question_type === "file") documents += 1;
        else information += 1;
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return { information, documents };
};

/** How many screens the client steps through. */
export const countSteps = (nodes: OutgoingIntakeFormNode[]): number =>
  flattenIntakeStepNodes(nodes).length;

export const intakeArtifact = (intake: OutgoingIntake): RequestArtifact => ({
  kind: "intake",
  id: intake.outgoing_intake_id,
  title: intake.title || "Intake"
});

export const previewArtifact = (intake: OutgoingIntake): RequestArtifact => ({
  kind: "preview",
  id: intake.outgoing_intake_id,
  title: `Preview · ${intake.title || "Intake"}`
});

export const parseTemplateArtifactId = (
  id: string
): { saved: boolean; templateId: string } =>
  id.startsWith("saved:")
    ? { saved: true, templateId: id.slice("saved:".length) }
    : { saved: false, templateId: id };

export type TemplateCategory = "saved" | "building_block" | "visa" | "form";

/**
 * The template groups a user browses. Building blocks and visa starters are
 * the Client Manager's raw material for composing intakes, not user-facing.
 */
export const TEMPLATE_GROUPS: readonly {
  id: TemplateCategory;
  label: string;
}[] = [
  { id: "saved", label: "Saved (Firm)" },
  { id: "form", label: "USCIS Forms" }
];

/** Whether a catalog template is one a user can browse and pick. */
export const isUserFacingTemplate = (t: OutgoingIntakeTemplate): boolean =>
  t.template_id !== "custom" &&
  TEMPLATE_GROUPS.some((g) => g.id === (t.category ?? "building_block"));

export const templateCategoryLabel = (
  category: TemplateCategory,
  template?: OutgoingIntakeTemplate
): string => {
  switch (category) {
    case "saved":
      return "Saved · firm";
    case "building_block":
      return "Building block";
    case "visa":
      return template?.visa
        ? `Visa starter · ${template.visa}`
        : "Visa starter";
    case "form":
      return template?.agency ? `${template.agency} form` : "USCIS form";
    default:
      return "";
  }
};

/** Filter templates by a free-text query over label, visa and agency. */
export const matchesTemplateQuery = (
  template: {
    label?: string;
    name?: string;
    visa?: string | null;
    agency?: string;
    description?: string | null;
  },
  query: string
): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    template.label,
    template.name,
    template.visa,
    template.agency,
    template.description
  ]
    .filter(Boolean)
    .some((s) => String(s).toLowerCase().includes(q));
};
